import { KpiGrid, type Column } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { pickName } from '../catalog/categories.ts';
import { indexOptions, type CatalogOption } from '../catalog/options.ts';
import { useOptions } from '../catalog/useCatalogQueries.ts';
import { StockValueTile } from '../dashboard/StockValueTile.tsx';
import type { ReportPeriod } from '../report-period.ts';
import { usePageContext } from '../usePageContext.ts';
import { QueryView } from '../QueryView.tsx';
import { deliveryRateColumn, numericColumn } from './columns.ts';
import { StatisticsTable } from './StatisticsTable.tsx';
import { StageTiles } from './StageTiles.tsx';
import {
  ORDER_STAGE_TILES,
  useStatisticsFormat,
  useStatisticsReport,
  type ProductsReport,
} from './statistics.ts';
import styles from './Statistics.module.css';

type ProductRow = ProductsReport['products'][number];
type OptionValueRow = NonNullable<ProductsReport['byOptionValue']>[number];
type SalesRow = ProductRow | OptionValueRow;

type Grouping = { readonly option: CatalogOption; readonly options: readonly CatalogOption[] };

const useSalesColumns = (currency: string) => {
  const { t } = usePageContext();
  const format = useStatisticsFormat(currency);
  return <Row extends SalesRow>(first: Column<Row>): readonly Column<Row>[] => [
    first,
    numericColumn('delivered', t('statistics.unitsDelivered'), (row) => format.count(row.delivered)),
    numericColumn('cost', t('statistics.purchaseTotal'), (row) => format.money(row.cost)),
    numericColumn('revenue', t('statistics.revenue'), (row) => format.money(row.revenue)),
    numericColumn('margin', t('statistics.margin'), (row) => format.money(row.margin)),
    deliveryRateColumn(t('statistics.deliveryRate'), format),
    numericColumn('returned', t('statistics.unitsReturned'), (row) => format.count(row.returned)),
  ];
};

const VariantTable = ({ report }: { report: ProductsReport }) => {
  const { t } = usePageContext();
  const columnsAfter = useSalesColumns(report.currency);
  return (
    <StatisticsTable
      title={t('statistics.productsTitle')}
      help={t('statistics.productsHelp')}
      columns={columnsAfter<ProductRow>({
        id: 'product',
        header: t('statistics.product'),
        primary: true,
        cell: (row) => (
          <span>
            {row.productName} <span className={styles['muted']}>{row.sku}</span>
          </span>
        ),
      })}
      rows={report.products}
      rowKey={(row) => row.variantId}
      emptyTitle={t('statistics.noProducts')}
    />
  );
};

const NO_VALUE_KEY = 'none';

const OptionValueTable = ({ report, grouping }: { report: ProductsReport; grouping: Grouping }) => {
  const { t, locale } = usePageContext();
  const columnsAfter = useSalesColumns(report.currency);
  const index = indexOptions(grouping.options, locale);
  const optionName = pickName(grouping.option.names, locale, grouping.option.code);
  return (
    <StatisticsTable
      title={t('statistics.optionTitle', { option: optionName })}
      help={t('statistics.optionHelp')}
      columns={columnsAfter<OptionValueRow>({
        id: 'value',
        header: optionName,
        primary: true,
        cell: (row) =>
          row.optionValueId === null ? t('statistics.noOptionValue') : index.valueName(row.optionValueId),
      })}
      rows={report.byOptionValue ?? []}
      rowKey={(row) => row.optionValueId ?? NO_VALUE_KEY}
      emptyTitle={t('statistics.noProducts')}
    />
  );
};

type ProductFiguresProps = { api: TypedClient; report: ProductsReport; grouping: Grouping | null };

const EVERY_PRODUCT = {};

const ProductFigures = ({ api, report, grouping }: ProductFiguresProps) => {
  const format = useStatisticsFormat(report.currency);
  return (
    <div className={styles['stack']}>
      <KpiGrid columns={3}>
        <StockValueTile api={api} scope={EVERY_PRODUCT} />
      </KpiGrid>
      <StageTiles
        stages={report.stages}
        order={ORDER_STAGE_TILES}
        labelPrefix="statistics.units_"
        format={format}
        loading={false}
      />
      {grouping === null ? (
        <VariantTable report={report} />
      ) : (
        <OptionValueTable report={report} grouping={grouping} />
      )}
    </div>
  );
};

export type ProductStatisticsProps = { api: TypedClient; period: ReportPeriod; groupBy: string | null };

export const ProductStatistics = ({ api, period, groupBy }: ProductStatisticsProps) => {
  const { t } = usePageContext();
  const options = useOptions(api).data?.items ?? [];
  const option = options.find((entry) => entry.id === groupBy);
  const grouping = option === undefined ? null : { option, options };
  const query = useStatisticsReport('products', period, { groupBy }, () =>
    api.get('/admin/statistics/products', {
      query: { ...period, ...(groupBy === null ? {} : { option_id: groupBy }) },
    }),
  );
  return (
    <QueryView query={query} label={t('statistics.tab_products')}>
      {(report) => <ProductFigures api={api} report={report} grouping={grouping} />}
    </QueryView>
  );
};
